import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { BusinessAnalytics } from "@xepp/business-analytics-sdk";
import { getUserIdFromToken } from "../../guards/common/authentification-functions";
import { notificationHubManager } from "../../services/signalr/notification-hub-manager";
import { salesIQService } from "../../services/salesIQ/salesIQ-service";
import { trackLogout } from "../../services/business-analytics/authEvents";

export enum UserState {
	ViewingAsGuest,
	LoggedIn,
	TokenExpired,
}

interface CurrentUserState {
	userState: UserState;
	userType: string | null;
	userName?: string;
	email?: string;
	firstName: string;
	lastName: string;
	countryCode: string;
	utilityId?: string;
	phoneNumber?: string;
	hasPin?: boolean;
	image?: string;
	customerType?: string;
}

/** When the current login happened — the logout event reports the session length from it. */
const LOGIN_AT_KEY = "loginAt";

const sessionDurationSeconds = (): number | null => {
	const loginAt = Number(sessionStorage.getItem(LOGIN_AT_KEY));
	return loginAt > 0 ? Math.round((Date.now() - loginAt) / 1000) : null;
};

const initialState: CurrentUserState = {
	userState: UserState.ViewingAsGuest,
	userType: null,
	userName: "",
	email: "",
	firstName: "",
	lastName:  "",
	countryCode: "",
	utilityId: "",
	image: "",
	customerType: undefined,
};

export const currentUserSlice = createSlice({
	name: "currentUser",
	initialState,
	reducers: {
		tokenExpired: state => {
			state.userState = UserState.TokenExpired;
			state.userType = null;
			sessionStorage.removeItem("token");
			sessionStorage.removeItem("refreshToken");
			sessionStorage.removeItem("reduxState");
			sessionStorage.removeItem(LOGIN_AT_KEY);
			salesIQService.closeChat();
			BusinessAnalytics.identify(null);
		},
		// A logout the user did not ask for (a ForceLogout push, say) is not a logout event.
		logOut: (state, action: PayloadAction<{ userInitiated?: boolean } | undefined>) => {
			if (action.payload?.userInitiated !== false) {
				trackLogout(getUserIdFromToken(sessionStorage.getItem("token") ?? ""), sessionDurationSeconds());
			}
			state.userState = UserState.ViewingAsGuest;
			state.userType = null;
			state.userName = "";
			sessionStorage.removeItem("token");
			sessionStorage.removeItem("refreshToken");
			sessionStorage.removeItem("reduxState");
			sessionStorage.removeItem(LOGIN_AT_KEY);

			// Stop SignalR connection on logout
			notificationHubManager.stop();
			salesIQService.closeChat();
			BusinessAnalytics.identify(null);
		},
		logIn: (state, payload: PayloadAction<{ token: string; userType: string; refreshToken: string }>) => {
			state.userState = UserState.LoggedIn;
			state.userType = payload.payload.userType;
			sessionStorage.setItem("token", payload.payload.token);
			sessionStorage.setItem("refreshToken", payload.payload.refreshToken);
			sessionStorage.setItem(LOGIN_AT_KEY, Date.now().toString());
			BusinessAnalytics.identify(getUserIdFromToken(payload.payload.token));
		},
		updateUsername: (state, payload: PayloadAction<{ userName: string }>) => {
			state.userName = payload.payload.userName;
		},
		updateCustomerInfo: (state,
			payload: PayloadAction<{ firstName: string, lastName: string, countryCode: string, phoneNumber: string, hasPin: boolean, image: string, email: string, customerType?: string }>) => {
			state.firstName = payload.payload.firstName;
			state.lastName = payload.payload.lastName;
			state.countryCode = payload.payload.countryCode;
			state.phoneNumber = payload.payload.phoneNumber;
			state.hasPin = payload.payload.hasPin;
			state.image = payload.payload.image;
			state.email = payload.payload.email;
			state.userName = payload.payload.email;
			state.customerType = payload.payload.customerType;
		},
		updateUserPin: (state, action: PayloadAction<{ hasPin: boolean }>) => {
            state.hasPin = action.payload.hasPin;
         },
		viewAsGuest: state => {
			state.userState = UserState.ViewingAsGuest;
			state.userType = null;
		},
	},
});

export const { logOut, logIn, viewAsGuest, tokenExpired, updateUsername, updateCustomerInfo, updateUserPin } = currentUserSlice.actions;

export default currentUserSlice.reducer;
